import React, { useState, useRef } from "react";
import { Upload, FileUp, X, AlertCircle, FileText, Database } from "lucide-react";
import CVBankPicker from "./CVBankPicker";

const ResumeUploadModal = ({ isOpen, onClose, onUpload, title, accept, jobId, onBankAttached }) => {
  const [files, setFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [tab, setTab] = useState("upload"); // "upload" | "bank"
  const fileInputRef = useRef(null);

  // Return null if modal is not open
  if (!isOpen) {
    return null;
  }

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFiles = Array.from(e.dataTransfer.files);
    console.log('Drop event triggered');
    console.log('Dropped files count:', droppedFiles.length);
    console.log('Dropped files:', droppedFiles.map(f => ({ name: f.name, type: f.type, size: f.size })));
    
    if (droppedFiles.length === 0) {
      console.log('No files dropped');
      return;
    }
    
    // Validate files
    const validTypes = ["application/pdf", "application/vnd.openxmlformats-officedocument.wordprocessingml.document"];
    const invalidFiles = droppedFiles.filter(
      (file) => !validTypes.includes(file.type)
    );

    if (invalidFiles.length > 0) {
      setError(`Invalid file type(s): ${invalidFiles.map(f => f.name).join(', ')}. Only PDF and DOCX files are allowed.`);
      return;
    }

    // Check file sizes (5MB limit)
    const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB in bytes
    const oversizedFiles = droppedFiles.filter((file) => file.size > MAX_FILE_SIZE);

    if (oversizedFiles.length > 0) {
      setError(`Files too large: ${oversizedFiles.map(f => f.name).join(', ')}. Maximum size is 5MB per file.`);
      return;
    }

    // Check total number of files (50 max for bulk processing)
    const totalFiles = files.length + droppedFiles.length;
    if (totalFiles > 50) {
      setError(`Too many files. You can upload up to 50 files at once. Currently have ${files.length}, trying to add ${droppedFiles.length}.`);
      return;
    }

    // Add files to the list
    setFiles((prev) => {
      const newFiles = [...prev, ...droppedFiles];
      console.log('Updated files list after drop:', newFiles.length, newFiles.map(f => f.name));
      return newFiles;
    });
    setError(null); // Clear any previous errors
  };

  const handleFileInput = (e) => {
    const selectedFiles = Array.from(e.target.files);
    console.log('File input triggered');
    console.log('Selected files count:', selectedFiles.length);
    console.log('Selected files:', selectedFiles.map(f => ({ name: f.name, type: f.type, size: f.size })));
    
    if (selectedFiles.length === 0) {
      console.log('No files selected');
      return;
    }
    
    // Validate files
    const validTypes = ["application/pdf", "application/vnd.openxmlformats-officedocument.wordprocessingml.document"];
    const invalidFiles = selectedFiles.filter(
      (file) => !validTypes.includes(file.type)
    );

    if (invalidFiles.length > 0) {
      setError(`Invalid file type(s): ${invalidFiles.map(f => f.name).join(', ')}. Only PDF and DOCX files are allowed.`);
      return;
    }

    // Check file sizes (5MB limit)
    const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB in bytes
    const oversizedFiles = selectedFiles.filter((file) => file.size > MAX_FILE_SIZE);

    if (oversizedFiles.length > 0) {
      setError(`Files too large: ${oversizedFiles.map(f => f.name).join(', ')}. Maximum size is 5MB per file.`);
      return;
    }

    // Check total number of files (50 max for bulk processing)
    const totalFiles = files.length + selectedFiles.length;
    if (totalFiles > 50) {
      setError(`Too many files. You can upload up to 50 files at once. Currently have ${files.length}, trying to add ${selectedFiles.length}.`);
      return;
    }

    // Add files to the list
    setFiles((prev) => {
      const newFiles = [...prev, ...selectedFiles];
      console.log('Updated files list:', newFiles.length, newFiles.map(f => f.name));
      return newFiles;
    });
    setError(null); // Clear any previous errors
    
    // Reset the file input to allow selecting the same file again
    e.target.value = '';
  };

  const validateAndAddFiles = (newFiles) => {
    setError("");
    const validTypes = ["application/pdf"];
    const invalidFiles = newFiles.filter(
      (file) => !validTypes.includes(file.type)
    );

    // if (invalidFiles.length > 0) {
    //   setError("Only PDF files are allowed");
    //   return;
    // }

    // Add files without size validation
    setFiles((prev) => [...prev, ...newFiles]);
  };

  const removeFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (files.length === 0) {
      setError("Please select at least one file");
      return;
    }

    // Additional validation before upload
    const validTypes = ["application/pdf", "application/vnd.openxmlformats-officedocument.wordprocessingml.document"];
    const invalidFiles = files.filter(
      (file) => !validTypes.includes(file.type)
    );

    if (invalidFiles.length > 0) {
      setError(`Invalid file type(s): ${invalidFiles.map(f => f.name).join(', ')}. Only PDF and DOCX files are allowed.`);
      return;
    }

    // Check for files larger than 5MB
    const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB in bytes
    const oversizedFiles = files.filter((file) => file.size > MAX_FILE_SIZE);

    if (oversizedFiles.length > 0) {
      setError(`Files too large: ${oversizedFiles.map(f => f.name).join(', ')}. Maximum size is 5MB per file.`);
      return;
    }

    if (files.length > 50) {
      setError("Maximum 50 files can be uploaded at once");
      return;
    }

    setIsUploading(true);
    setError(null);
    try {
      console.log('Uploading files:', files.length, files.map(f => f.name));
      await onUpload(files);
      setFiles([]);
      onClose();
    } catch (err) {
      console.error('Upload error:', err);
      
      // Handle specific error for invalid resumes
      if (err.response?.data?.errors) {
        const invalidFiles = err.response.data.errors;
        setError({
          message: 'One or more resumes are invalid',
          details: invalidFiles.map(file => `${file.fileName}: ${file.reason}`).join('\n')
        });
      } else {
        setError({
          message: err.response?.data?.message || 'Failed to upload resumes',
          details: null
        });
      }
    } finally {
      setIsUploading(false);
    }
  };

  const handleClose = () => {
    setFiles([]);
    setError(null);
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto"
      aria-labelledby="modal-title"
      role="dialog"
      aria-modal="true"
    >
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
        onClick={handleClose}
      ></div>

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl sm:p-6">
          {/* Close button */}
          <button
            onClick={handleClose}
            className="absolute right-4 top-4 text-gray-400 hover:text-gray-500"
          >
            <X className="h-6 w-6" />
          </button>

          {/* Title */}
          <div className="mb-4">
            <h3 className="text-lg font-medium text-gray-900">
              {title === 'offerLetter' ? 'Upload Offer Letter Template' : 'Add Resumes'}
            </h3>
            {title === 'offerLetter' && (
              <p className="text-sm text-gray-500 mt-1">Upload a PDF or DOCX template. AI will analyse its structure and use it as a style guide when generating offer letters.</p>
            )}
          </div>

          {/* Tab toggle: Upload new vs Pick from CV bank (hidden for offer letter templates) */}
          {title !== 'offerLetter' && (
          <div className="flex gap-2 mb-5 border-b border-slate-200">
            <button
              type="button"
              onClick={() => setTab("upload")}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition ${
                tab === "upload" ? "border-orange-500 text-orange-600" : "border-transparent text-slate-500 hover:text-slate-700"
              }`}
            >
              <Upload className="w-4 h-4" /> Upload new
            </button>
            <button
              type="button"
              onClick={() => setTab("bank")}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition ${
                tab === "bank" ? "border-orange-500 text-orange-600" : "border-transparent text-slate-500 hover:text-slate-700"
              }`}
            >
              <Database className="w-4 h-4" /> Pick from CV bank
            </button>
          </div>
          )}

          {tab === "bank" && (
            <CVBankPicker
              jobId={jobId}
              onAttached={(count) => {
                onBankAttached?.(count);
                onClose?.();
              }}
            />
          )}

          {tab === "upload" && (
          /* Content */
          <div className="space-y-6">
            {error && (
              <div className="flex items-center gap-2 text-red-500 bg-red-50 p-3 rounded-lg">
                <AlertCircle className="w-5 h-5" />
                <div>
                  <p className="font-medium">{error.message}</p>
                  {error.details && (
                    <pre className="mt-2 text-sm whitespace-pre-wrap">{error.details}</pre>
                  )}
                </div>
              </div>
            )}

            <div
              className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors
                ${
                  isDragging
                    ? "border-orange-500 bg-orange-50"
                    : "border-gray-300 hover:border-orange-500"
                }
              `}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  fileInputRef.current.click();
                }
              }}
              tabIndex={0}
              role="button"
              aria-label="Upload files by clicking or dragging and dropping"
            >
              <input
                id="fileInput"
                type="file"
                multiple={true}
                accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                className="hidden"
                onChange={handleFileInput}
                ref={fileInputRef}
                onClick={(e) => {
                  // Ensure the input is properly reset
                  e.target.value = '';
                }}
              />

              <FileUp className={`w-12 h-12 mx-auto mb-4 transition-colors ${
                isDragging ? "text-orange-500" : "text-gray-400"
              }`} />
              {title === "offerLetter" ? (
                <>
                  <p className="text-lg mb-2">
                    {isDragging
                      ? <span className="text-orange-500 font-medium">Drop template here</span>
                      : <>Drag and drop your template here or <span className="text-orange-500">browse</span></>}
                  </p>
                  {!isDragging && (
                    <div className="text-sm text-gray-500 space-y-1 mb-4">
                      <p className="font-medium">Accepted formats:</p>
                      <ul>
                        <li>• PDF or DOCX files only</li>
                        <li>• Maximum 5MB</li>
                        <li>• One file at a time</li>
                      </ul>
                    </div>
                  )}
                  {!isDragging && (
                    <button
                      onClick={() => fileInputRef.current.click()}
                      className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
                    >
                      {files.length > 0 ? `Change File (${files[0]?.name})` : 'Select Template File'}
                    </button>
                  )}
                </>
              ) : (
                <>
                  <p className="text-lg mb-2">
                    {isDragging ? (
                      <span className="text-orange-500 font-medium">Drop files here to upload</span>
                    ) : (
                      <>
                        Drag and drop multiple resumes here or{" "}
                        <span className="text-orange-500">browse</span>
                      </>
                    )}
                  </p>
                  {!isDragging && (
                    <div className="text-sm text-gray-500 space-y-1 mb-4">
                      <p className="font-medium">Instructions:</p>
                      <ul>
                        <li>• Only PDF & DOCX files are accepted</li>
                        <li>• Maximum 50 files at once</li>
                        <li>• Each file should be less than 5MB</li>
                        <li>• Make sure your resume is clearly readable</li>
                        <li>• You can select multiple files or drag & drop</li>
                        <li>• Hold Ctrl/Cmd to select multiple files</li>
                      </ul>
                    </div>
                  )}
                  
                  {!isDragging && (
                    <div className="space-y-2">
                      <button
                        onClick={() => fileInputRef.current.click()}
                        className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
                      >
                        {files.length > 0 ? `Add More Files (${files.length} selected)` : 'Select Multiple Files'}
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>

            {files.length > 0 && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-medium">Selected Files ({files.length})</h3>
                  <button
                    onClick={() => setFiles([])}
                    className="text-sm text-red-600 hover:text-red-800 transition-colors"
                  >
                    Clear All
                  </button>
                </div>
                <div className="max-h-48 overflow-y-auto space-y-2">
                  {files.map((file, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between bg-gray-50 p-2 rounded"
                    >
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-gray-400" />
                        <span className="text-sm">{file.name}</span>
                        <span className="text-xs text-gray-500">
                          ({(file.size / (1024 * 1024)).toFixed(2)} MB)
                        </span>
                        <span className={`text-xs px-1 py-0.5 rounded ${
                          file.type === 'application/pdf' 
                            ? 'bg-red-100 text-red-700' 
                            : 'bg-blue-100 text-blue-700'
                        }`}>
                          {file.type === 'application/pdf' ? 'PDF' : 'DOCX'}
                        </span>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          removeFile(index);
                        }}
                        className="p-1 hover:text-red-500 transition-colors"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
                
                {/* File Summary */}
                <div className="bg-blue-50 p-3 rounded-lg">
                  <div className="text-sm text-blue-800">
                    <div className="flex justify-between items-center">
                      <span>Total Files: {files.length}</span>
                      <span>Total Size: {(files.reduce((acc, file) => acc + file.size, 0) / (1024 * 1024)).toFixed(2)} MB</span>
                    </div>
                    <div className="flex gap-4 mt-1 text-xs">
                      <span>PDF: {files.filter(f => f.type === 'application/pdf').length}</span>
                      <span>DOCX: {files.filter(f => f.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document').length}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="flex justify-end gap-4">
              <button
                onClick={handleClose}
                disabled={isUploading}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={files.length === 0 || isUploading}
                className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {isUploading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                    <span>Uploading...</span>
                  </>
                ) : (
                  <span>{title === 'offerLetter' ? 'Upload Template' : 'Upload Resumes'}</span>
                )}
              </button>
            </div>
          </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResumeUploadModal;